import React, { useMemo } from 'react';

import { TrussEnvelope } from '@paragon/contract-library';
import { Line2D, Point2D } from '@paragon/geometry-library';
import { calculateTrussEnvelopeShapeWithEdgeSegments, TrussEnvelopeShapeWithEdgeSegments } from '@paragon/layout';

import { ManualRenderModes } from '../../../library/renderModes';
import { EnvelopeRenderMode } from '../../../store/slices/visualOptionsSlice';
import { EnvelopeEndLabels } from '../EnvelopeEndLabels';
import { raycastLayoutCoordinatesPolygon } from '../raycastLayoutCoordinatesPolygon';
import { getEnvelopeColor, TrussEnvelopeStyle } from './colors';
import { splitTrussEnvelopePolygon } from './split-truss-envelope-polygon';
import { TrussEnvelopeEdgeVisuals } from './TrussEnvelopeEdgeVisuals';
import { TrussEnvelopeLeftSideIndicator } from './TrussEnvelopeLeftSideIndicator';
import { TrussEnvelopeSolidVisual } from './TrussEnvelopeSolidVisual';
import { useTrussEnvelopeEventHandlers } from './useTrussEnvelopeEventHandlers';

export const NEVER_RAYCAST = () => {};

export function TrussEnvelopeVisual(props: {
  envelope: TrussEnvelope;
  forciblyEmphasised?: boolean;

  style: TrussEnvelopeStyle;

  in3D: boolean;
  renderMode: ManualRenderModes<EnvelopeRenderMode>;

  showEndLabels?: boolean;

  inert?: boolean;
}) {
  const geometry = useMemo(() => calculateTrussEnvelopeShapeWithEdgeSegments(props.envelope), [props.envelope]);

  // cast is "safe" since we check return if `geometry == null` below
  const { shape, ...segments } = geometry ?? ({} as TrussEnvelopeShapeWithEdgeSegments);

  const userData = useMemo(
    () => ({ raycastPolygon: shape, raycastElevation: props.envelope.elevation }),
    [shape, props.envelope.elevation],
  );

  const eventHandlers = useTrussEnvelopeEventHandlers(props.envelope, { inert: props.inert });

  if (geometry == null || shape == null) return null;

  const color = getEnvelopeColor(props.style);
  const showTrussSolid = props.renderMode === 'trussSolids';

  const envelopeLeftPoint = Point2D.fromObject(props.envelope.leftPoint);
  const envelopeRightPoint = Point2D.fromObject(props.envelope.rightPoint);

  const trueEnvelopeBounds = [
    Line2D.fromPoints(
      envelopeLeftPoint.projectOntoLine(segments.backSegment.toLine()),
      envelopeLeftPoint.projectOntoLine(segments.frontSegment.toLine()),
    ),
    Line2D.fromPoints(
      envelopeRightPoint.projectOntoLine(segments.backSegment.toLine()),
      envelopeRightPoint.projectOntoLine(segments.frontSegment.toLine()),
    ),
  ] as const;

  const [trueEnvelopeSegments, overhangSegments] = splitTrussEnvelopePolygon(
    shape,
    segments.middleSegment.toLine(),
    trueEnvelopeBounds,
  );

  // check if we need to add dividing segments between the truss envelope proper and overhang area
  for (const boundsLine of trueEnvelopeBounds) {
    if (trueEnvelopeSegments.some((segment) => segment.toLine().equals(boundsLine))) continue;

    const divisionSegments = shape.findSegmentIntersectionsWithLine(boundsLine);
    if (divisionSegments.length === 1) trueEnvelopeSegments.push(divisionSegments[0]);
  }

  return (
    <>
      {/* event target */}
      <object3D {...eventHandlers} userData={userData} raycast={raycastLayoutCoordinatesPolygon}>
        {/* these objects may extend outside the 2D envelope bounds, and we want them to be part of the event target */}
        {showTrussSolid && (
          <TrussEnvelopeSolidVisual envelope={props.envelope} color={color} renderEdges={props.in3D} />
        )}
      </object3D>

      <TrussEnvelopeEdgeVisuals
        envelope={props.envelope}
        renderMode={props.renderMode}
        style={props.style}
        in3D={props.in3D}
        showPlyDividers={!(props.in3D && props.renderMode === 'components')}
        envelopeSegments={trueEnvelopeSegments}
        overhangSegments={overhangSegments}
      />

      <TrussEnvelopeLeftSideIndicator
        definingPoint={envelopeLeftPoint}
        segments={segments}
        elevation={props.envelope.elevation}
      />

      {props.showEndLabels && (
        <EnvelopeEndLabels envelope={props.envelope} elevation={props.envelope.elevation} alwaysOnTop={!props.in3D} />
      )}
    </>
  );
}
